import { useNavigate, useLocation } from 'react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field';
import ArrowleftIcon from '@/components/svg/ArrowleftIcon';
import { Button } from '@/components/ui/button';
import { useCustomParam } from '@/hooks';
import { useStartUserEmailAuthSession } from '@/features/auth/mutations';
import { FormEvent, useState } from 'react';
import { Input } from '@/components/ui/input';
import { validateEmail } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';

const AlreadyHasCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const getParam = useCustomParam();
  const rid = getParam('rid');
  const referrer = getParam('referrer');
  const username = getParam('username');
  const ridOrRef = rid || referrer;
  const [email, setEmail] = useState('');
  const [manualRedirectUrl, setManualRedirectUrl] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState(false);

  const startUserEmailAuthSession = useStartUserEmailAuthSession();

  const handleNavigateBack = () => {
    return navigate(location.pathname + location.search, {
      state: { view: 'email' },
      replace: true,
    });
  };

  const handleSignInWithCode = async () => {
   setCodeError(false);
   setEmailError('');
   setError('');
   setManualRedirectUrl(null);

    if (!ridOrRef) {
      setError('Referrer or rid is required');
      return;
    }

    if (!code || code.length !== 6) {
      setCodeError(true);
      return;
    }

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    
    try {
        validateEmail(email);
    } catch (error) {
        setEmailError((error as Error).message);
        return;
    }

    startUserEmailAuthSession.mutate(
      {
        code,
        email,
        username: username || null,
        referrer: ridOrRef || null,
      },
      {
        onSuccess: (data, variables) => {
          setError('');

          setTimeout(() => {
            let redirectUrl = '';

            if (variables.referrer?.indexOf('?') != -1 || variables.referrer?.indexOf('&') != -1) {
              redirectUrl = variables.referrer + `&token=${data.token}&username=${data.username}`;
            } else {
              redirectUrl = variables.referrer + `?token=${data.token}&username=${data.username}`;
            }
          
            setManualRedirectUrl(redirectUrl);
          }, 2000);
        },
        onError: (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Code is invalid or expired. Please resend the code. If this persists, please contact us at contact@getcollectr.com';
          setError(errorMessage);
          setCode('');
        },
      }
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSignInWithCode();
  };

  return (
    <div className='flex flex-col justify-center my-auto min-h-screen'>
      <Card className='sm:max-w-md w-full mx-auto sm:min-h-min min-h-screen sm:rounded-xl rounded-none'>
        <CardHeader>
          <div className='flex justify-between'>
            <div className='mx-auto'>
              <img className='dark:hidden h-12 text-center block' src={'/images/black-logo.png'} alt='Collectr' />
              <img className='hidden h-12 text-center dark:block' src={'/images/white-logo.png'} alt='Collectr' />
            </div>
          </div>
        </CardHeader>
        <CardContent className='sm:mt-0 mt-4'>
          <div className='flex flex-col justify-center'>
            {error && <p className='text-destructive text-sm font-normal mb-4'>{error}</p>}

            <FieldSet>   
              <form onSubmit={handleSubmit}>
                <FieldGroup>
                  <Field className='flex flex-col items-center justify-center'>
                      <FieldLabel htmlFor='code'>Code</FieldLabel>

                      <Input id='code' required type='number' className='md:text-sm text-base' placeholder='123456' value={code} maxLength={6} aria-invalid={codeError ? 'true' : 'false'} onChange={(e) => setCode(e.target.value)} />
                      {codeError && <FieldError>Enter a 6 digit code.</FieldError>}
                  </Field>

                  <Field>
                      <FieldLabel htmlFor='email'>Email</FieldLabel>
                      <Input id='email' required type='email' className='md:text-sm text-base' placeholder='you@example.com' value={email} aria-invalid={emailError ? 'true' : 'false'} onChange={(e) => setEmail(e.target.value.trim())} />
                      {emailError && <FieldError>{emailError}</FieldError>}
                  </Field>

                  <Field>
                      <Button className='w-full' type='submit' variant={'brandPrimary'} onClick={handleSignInWithCode} isLoading={startUserEmailAuthSession.isPending} disabled={startUserEmailAuthSession.isPending}>
                          Verify Code
                      </Button>

                  </Field>
                </FieldGroup>
              </form>
              
              {manualRedirectUrl && (
                <a href={manualRedirectUrl} target='_blank' rel='noopener noreferrer'>
                  <Button className='w-full' type='submit' variant={'outline'}  isLoading={startUserEmailAuthSession.isPending} disabled={startUserEmailAuthSession.isPending}>
                    If you haven't been redirected, click here
                    <ArrowUpRight />
                  </Button>
                </a>
              )}
            </FieldSet>
          </div>

          <Button className='w-full mt-8' variant={'ghost'} onClick={handleNavigateBack}>
            <ArrowleftIcon />
            Go back to login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AlreadyHasCodePage;
